import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, toastError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { SetupSummaryDocuments } from "./SetupSummaryDocuments";

type Step = "welcome" | "company" | "organization" | "users" | "workflow" | "review";
type WizardData = {
  tenant: { name: string; slug: string; default_timezone: string; currency: string; status: string };
  progress: { current_step: Step; started_at?: string | null; last_saved_at?: string | null };
  counts: { companies: number; branches: number; users: number };
  readiness: { completion_pct: number; overall_status: string; incomplete_keys: string[] };
};

const STEPS: Array<{ key: Step; label: string }> = [
  { key: "welcome", label: "Welcome" },
  { key: "company", label: "Company" },
  { key: "organization", label: "Branches" },
  { key: "users", label: "Users" },
  { key: "workflow", label: "Workflow" },
  { key: "review", label: "Review" },
];

export function OwnerOnboardingWizard() {
  const navigate = useNavigate();
  const [data, setData] = useState<WizardData | null>(null);
  const [step, setStep] = useState<Step>("welcome");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", default_timezone: "UTC", currency: "CAD" });

  useEffect(() => {
    void api.get("/onboarding/wizard").then((r) => {
      const next = r.data as WizardData;
      setData(next);
      setStep(next.progress.current_step || "welcome");
      setForm({
        name: next.tenant.name || "",
        default_timezone: next.tenant.default_timezone || "UTC",
        currency: next.tenant.currency || "CAD",
      });
    }).catch(toastError);
  }, []);

  const index = useMemo(() => STEPS.findIndex((s) => s.key === step), [step]);
  const saveStep = async (next: Step) => {
    setSaving(true);
    try {
      const r = await api.patch("/onboarding/wizard/progress", { current_step: next });
      setData(r.data);
      setStep(next);
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };
  const saveProfile = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/onboarding/wizard/profile", {
        name: form.name.trim(),
        default_timezone: form.default_timezone.trim(),
        currency: form.currency.trim().toUpperCase(),
      });
      setData(r.data);
      toast.success("Company profile saved");
      await saveStep("organization");
    } catch (e) {
      toastError(e);
      setSaving(false);
    }
  };
  const openSetup = async (path: string, resume: Step) => {
    await saveStep(resume);
    navigate(path);
  };
  if (!data) return <div className="rounded-lg border bg-white p-5 text-sm text-slate-500">Loading guided setup…</div>;

  return (
    <section className="mb-4 rounded-xl border border-sky-200 bg-white p-5 shadow-sm" data-testid="owner-onboarding-wizard">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">Guided owner setup</p>
          <h2 className="mt-1 text-xl font-semibold">Review, correct, and confirm your workspace</h2>
          <p className="mt-1 text-sm text-slate-600">Your pre-filled information remains editable. Progress saves to this tenant and resumes where you stopped.</p>
        </div>
        <span className="rounded-full bg-sky-50 px-3 py-1 text-sm font-medium text-sky-800">{data.readiness.completion_pct}% ready</span>
      </div>

      <ol className="mt-5 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {STEPS.map((item, i) => (
          <li key={item.key}>
            <button type="button" onClick={() => void saveStep(item.key)}
              className={`w-full rounded-md border px-3 py-2 text-left text-sm ${item.key === step ? "border-sky-500 bg-sky-50 text-sky-900" : "border-slate-200 text-slate-600"}`}>
              <span className="mr-1 text-xs">{i + 1}.</span> {item.label}
            </button>
          </li>
        ))}
      </ol>

      <div className="mt-5 rounded-lg border border-slate-200 p-4">
        {step === "welcome" && <div>
          <h3 className="font-semibold">Welcome to {data.tenant.name || "RemediKeep"}</h3>
          <p className="mt-2 text-sm text-slate-600">We will first verify the company information entered by the platform administrator, then review branches, users, workflow, and launch readiness.</p>
        </div>}

        {step === "company" && <div className="grid gap-4 md:grid-cols-3">
          <div><Label>Company display name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Timezone (IANA)</Label><Input value={form.default_timezone} onChange={(e) => setForm({ ...form, default_timezone: e.target.value })} placeholder="America/Edmonton" /></div>
          <div><Label>Currency</Label><Input maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} placeholder="CAD" /></div>
          <p className="md:col-span-3 text-xs text-slate-500">Permanent workspace ID and slug ({data.tenant.slug}) are protected. Name, timezone, and currency changes are audited.</p>
        </div>}

        {step === "organization" && <div>
          <h3 className="font-semibold">Companies and branches</h3>
          <p className="mt-2 text-sm text-slate-600">{data.counts.companies} company record(s) and {data.counts.branches} branch record(s) currently exist. Review spelling and links; archive historical branches instead of deleting them.</p>
          <Button className="mt-3" variant="outline" onClick={() => void openSetup("/admin/structure", "organization")}>Review company and branches</Button>
        </div>}

        {step === "users" && <div>
          <h3 className="font-semibold">Users and access</h3>
          <p className="mt-2 text-sm text-slate-600">{data.counts.users} active tenant membership(s) found. Add users, assign roles and branches, and download each display-once login PDF.</p>
          <Button className="mt-3" variant="outline" onClick={() => void openSetup("/admin/users", "users")}>Review users</Button>
        </div>}

        {step === "workflow" && <div>
          <h3 className="font-semibold">Workflow and operating rules</h3>
          <p className="mt-2 text-sm text-slate-600">Confirm the CRM template and then review terminology, statuses, commissions, mileage, expenses, and invoicing in Configuration.</p>
          <Button className="mt-3" variant="outline" onClick={() => void openSetup("/config", "workflow")}>Review configuration</Button>
        </div>}

        {step === "review" && <div>
          <h3 className="font-semibold">Launch review</h3>
          <p className="mt-2 text-sm text-slate-600">Authoritative readiness is {data.readiness.completion_pct}%. Outstanding controls: {data.readiness.incomplete_keys.length ? data.readiness.incomplete_keys.join(", ") : "None"}.</p>
          <p className="mt-2 text-xs text-slate-500">Finishing this tour does not bypass readiness controls. Final onboarding completion remains available only when the server verifies every required item.</p>
          <SetupSummaryDocuments />
        </div>}
      </div>

      <div className="mt-4 flex justify-between gap-3">
        <Button variant="outline" disabled={saving || index <= 0} onClick={() => void saveStep(STEPS[index - 1].key)}>Back</Button>
        {step === "company" ? (
          <Button disabled={saving || !form.name.trim() || form.currency.trim().length !== 3} onClick={() => void saveProfile()}>{saving ? "Saving…" : "Save and continue"}</Button>
        ) : index < STEPS.length - 1 ? (
          <Button disabled={saving} onClick={() => void saveStep(STEPS[index + 1].key)}>{saving ? "Saving…" : "Save and continue"}</Button>
        ) : (
          <Button disabled={saving} onClick={() => void saveStep("review")}>Finish later</Button>
        )}
      </div>
    </section>
  );
}
